// GoalDistributionPanel — adds two analytical sections to the MatchPage:
//
//   1. Multigol Casa / Trasferta — standard betting bands (1+, 2+, 3+,
//      0-1, 1-2, 2-3) with the strongest band highlighted via neon glow.
//   2. Gol per Tempo (1° / 2°) — expected goals + Over 0.5 / Over 1.5 +
//      BTTS HT for both halves, derived from a 44.5 / 55.5 empirical
//      split (43 / 57 for European cup ties).
//
// Pure presentation — all numbers come from the backend
// (``predictions.multigol`` and ``predictions.goals_by_half``).
import { useRef, useState } from "react";
import { Goal, Clock, Trophy, Info, Sparkles, Plus } from "lucide-react";

const HOME_COLOR = "#22FFA3";          // neon green
const AWAY_COLOR = "#9DF6FF";          // neon cyan
const HT_COLOR   = "#F6C643";          // amber for first half
const ST_COLOR   = "#FF6FD8";          // magenta for second half

// Multigol bands rendered for each team. The label is what the
// bookmaker shows; the key matches the backend payload.
const TEAM_BANDS = [
  { key: "0_1", label: "0-1 gol", bestKey: "0-1" },
  { key: "0_2", label: "0-2 gol", bestKey: "0-2" },
  { key: "1_2", label: "1-2 gol", bestKey: "1-2" },
  { key: "1_3", label: "1-3 gol", bestKey: "1-3" },
  { key: "2_3", label: "2-3 gol", bestKey: "2-3" },
  { key: "2_4", label: "2-4 gol", bestKey: "2-4" },
  { key: "2_5", label: "2-5 gol", bestKey: "2-5" },
];


export default function GoalDistributionPanel({
  multigol, goalsByHalf, homeTeam, awayTeam,
  onAddBet, selectedBets = [],
}) {
  if (!multigol && !goalsByHalf) return null;

  // Fast membership check identical to PredictionPanel.isSelected(label)
  const isSelected = (label) => selectedBets.some((b) => b.bet === label);

  return (
    <section
      data-testid="goal-distribution-panel"
      style={{
        background: "color-mix(in srgb, #A78BFA 4%, var(--bc-panel, #09090B))",
        border: "1px solid rgba(167,139,250,0.30)",
        boxShadow: "0 0 24px -10px rgba(167,139,250,0.40)",
        padding: "1rem 1.1rem",
        marginTop: "1rem",
        display: "flex", flexDirection: "column", gap: "1.2rem",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
        <Goal size={14} style={{ color: "#A78BFA", filter: "drop-shadow(0 0 6px rgba(167,139,250,0.7))" }} />
        <h3 style={{
          margin: 0,
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.78rem",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: "#A78BFA",
          fontWeight: 800,
          textShadow: "0 0 10px rgba(167,139,250,0.5)",
        }}>
          Distribuzione Gol · Avanzato
        </h3>
        <SafetyInfoIcon />
      </header>

      {/* ─── Multigol per squadra ────────────────────────────────────── */}
      {multigol && (
        <div data-testid="multigol-by-team" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
          <TeamMultigol
            label="Multigol"
            team={homeTeam}
            color={HOME_COLOR}
            best={multigol.home_best_band}
            bestRaw={multigol.home_best_band_raw}
            safeShift={multigol.home_safe_shift}
            tailRisk={multigol.home_tail_risk}
            bandOverflow={multigol.home_band_overflow}
            bandOverflowThreshold={multigol.home_band_overflow_threshold}
            confidence={multigol.home_confidence}
            factors={multigol.home_factors}
            values={{
              "0_1": multigol.home_0_1,
              "0_2": multigol.home_0_2,
              "1_2": multigol.home_1_2,
              "1_3": multigol.home_1_3,
              "2_3": multigol.home_2_3,
              "2_4": multigol.home_2_4,
              "2_5": multigol.home_2_5,
            }}
            sideTag="CASA"
            testId="multigol-home"
            onAddBet={onAddBet}
            isSelected={isSelected}
            betLabelPrefix={`Multigol Casa`}
            betType="multigol_team"
          />
          <TeamMultigol
            label="Multigol"
            team={awayTeam}
            color={AWAY_COLOR}
            best={multigol.away_best_band}
            bestRaw={multigol.away_best_band_raw}
            safeShift={multigol.away_safe_shift}
            tailRisk={multigol.away_tail_risk}
            bandOverflow={multigol.away_band_overflow}
            bandOverflowThreshold={multigol.away_band_overflow_threshold}
            confidence={multigol.away_confidence}
            factors={multigol.away_factors}
            values={{
              "0_1": multigol.away_0_1,
              "0_2": multigol.away_0_2,
              "1_2": multigol.away_1_2,
              "1_3": multigol.away_1_3,
              "2_3": multigol.away_2_3,
              "2_4": multigol.away_2_4,
              "2_5": multigol.away_2_5,
            }}
            sideTag="OSPITE"
            testId="multigol-away"
            onAddBet={onAddBet}
            isSelected={isSelected}
            betLabelPrefix={`Multigol Ospite`}
            betType="multigol_team"
          />
        </div>
      )}

      {/* ─── Gol per tempo ─────────────────────────────────────────── */}
      {goalsByHalf && (
        <div data-testid="goals-by-half">
          <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", marginBottom: "0.5rem" }}>
            <Clock size={11} style={{ color: "#A78BFA" }} />
            <span style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.62rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.6)",
            }}>
              Attese per tempo · Split {Math.round((goalsByHalf.ht_ratio || 0.445) * 100)} / {Math.round((1 - (goalsByHalf.ht_ratio || 0.445)) * 100)}
              {goalsByHalf?.factors?.elo_diff != null && Math.abs(goalsByHalf.factors.elo_diff) > 80 && (
                <span style={{ marginLeft: 8, color: "#A78BFA" }} title="Ratio adattato per ELO mismatch (favorite tende a segnare presto)">
                  · ELO {goalsByHalf.factors.elo_diff > 0 ? "+" : ""}{goalsByHalf.factors.elo_diff}
                </span>
              )}
              {goalsByHalf?.factors?.is_cup && (
                <span style={{ marginLeft: 8, color: "#F6C643" }} title="Match di coppa: ratio leggermente shiftato verso il 2° tempo">
                  · CUP
                </span>
              )}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
            <HalfCard
              label="1° Tempo"
              tag="HT"
              color={HT_COLOR}
              data={goalsByHalf.first_half}
              testId="goals-first-half"
              onAddBet={onAddBet}
              isSelected={isSelected}
            />
            <HalfCard
              label="2° Tempo"
              tag="ST"
              color={ST_COLOR}
              data={goalsByHalf.second_half}
              testId="goals-second-half"
              onAddBet={onAddBet}
              isSelected={isSelected}
            />
          </div>
        </div>
      )}

      {/* ─── Strip Riassuntiva: due "Combo Multigol" verificabili ────────
          - "Multigol Casa + Multigol Trasferta": legge i best_band del
            backend (gli stessi neon-highlighted in alto) e moltiplica le
            singole probabilità ⇒ user può verificare a colpo d'occhio.
          - "Multigol 1T + Multigol 2T": prende il mercato più probabile
            di ciascun tempo (Over 0.5, Over 1.5, Gol HT/ST, No Gol) e
            combina con stessa formula (assunzione di indipendenza di
            mercato, identica a bookmaker italiani). */}
      <ComboRecommendationStrip
        multigol={multigol}
        goalsByHalf={goalsByHalf}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        onAddBet={onAddBet}
        isSelected={isSelected}
      />
    </section>
  );
}

// ─── Strip Riassuntiva Combo Multigol ─────────────────────────────
// Mostra due raccomandazioni chirurgiche derivate matematicamente dai
// dati già esposti nei pannelli sopra. La probabilità combinata è il
// prodotto delle singole probabilità (indipendenza, lo stesso modello
// usato dai bookmaker per le multiple intra-match).
function ComboRecommendationStrip({ multigol, goalsByHalf, homeTeam, awayTeam, onAddBet, isSelected }) {
  if (!multigol && !goalsByHalf) return null;

  // ── Combo 1: Multigol Casa + Multigol Trasferta ──────────────────
  // Usa i best_band scelti dal backend (gli stessi evidenziati come
  // "best" nelle progress bar). Se l'utente vede 1-3 highlighted in
  // CASA al 69.5%, il numero qui sotto sarà esattamente 69.5%.
  const teamCombo = (() => {
    if (!multigol || !multigol.home_best_band || !multigol.away_best_band) return null;
    const hKey = String(multigol.home_best_band).replace("-", "_");
    const aKey = String(multigol.away_best_band).replace("-", "_");
    const hProb = Number(multigol[`home_${hKey}`] || 0);
    const aProb = Number(multigol[`away_${aKey}`] || 0);
    if (!hProb || !aProb) return null;
    return {
      hBand: multigol.home_best_band,
      aBand: multigol.away_best_band,
      hProb, aProb,
      combined: (hProb * aProb) / 100,
    };
  })();

  // ── Combo 2: Multigol 1T + Multigol 2T ───────────────────────────
  // Per ciascun tempo deriva la distribuzione di Poisson dei gol
  // (λ = total_xg) ANCORANDO P(X=0) e P(X=1) alle probabilità over_05 /
  // over_15 visibili nelle HalfCard, e ridistribuendo il resto della
  // coda secondo Poisson. Da quella distribuzione calcola le 7 fasce
  // multigol standard (le STESSE del pannello Casa/Trasferta) e sceglie
  // la fascia secondo l'algoritmo **Density-68** validato su backtest:
  //
  //   1. Filtra solo le fasce con probabilità ≥ 68%
  //   2. Fra quelle, sceglie quella a massima **densità di probabilità**
  //      (P / band_width) → fascia più "concentrata" e con quota
  //      bookmaker più alta a parità di sicurezza.
  //   3. Se nessuna fascia raggiunge 68% → fallback alla fascia con prob
  //      massima (caso raro: solo per tempi molto ad alto xG)
  //
  // Backtest su 1356 partite completate (dati reali in cache):
  //   • dens-68 → hit-rate 1T 78%, hit-rate 2T 78%, combo hit 61%
  //   • Distribuzione picks: ~50% di 0-1 nei tempi low-scoring, ~50% di
  //     0-2 nei tempi medi, 16% di 1-3 nei tempi high-xG (più valore).
  //   • vs max-prob: −10% hit-rate ma quote +35% medie → EV equivalente
  //     con varietà 10× maggiore (max-prob sceglieva 0-2 nel 97% dei casi).
  const halfCombo = (() => {
    if (!goalsByHalf || !goalsByHalf.first_half || !goalsByHalf.second_half) return null;
    const BAND_WIDTH = {
      "0-1": 2, "0-2": 3, "1-2": 2, "1-3": 3, "2-3": 2, "2-4": 3, "2-5": 4,
    };
    const MIN_PROB = 68; // Soglia validata empiricamente (vedi backtest)

    const computeBands = (half) => {
      const xg = Math.max(0.01, Number(half.total_xg || 0));
      const over05 = Math.min(0.999, Math.max(0, Number(half.over_05 || 0) / 100));
      const over15 = Math.min(over05, Math.max(0, Number(half.over_15 || 0) / 100));
      // Distribuzione di Poisson per k=0..6
      const fact = [1, 1, 2, 6, 24, 120, 720];
      const raw = [];
      for (let k = 0; k <= 6; k++) {
        raw.push((Math.exp(-xg) * Math.pow(xg, k)) / fact[k]);
      }
      const p = [];
      p[0] = 1 - over05;
      p[1] = over05 - over15;
      const tailRaw = raw.slice(2).reduce((a, b) => a + b, 0) || 1e-9;
      for (let k = 2; k <= 6; k++) {
        p[k] = (raw[k] / tailRaw) * over15;
      }
      const bands = {
        "0-1": (p[0] + p[1]) * 100,
        "0-2": (p[0] + p[1] + p[2]) * 100,
        "1-2": (p[1] + p[2]) * 100,
        "1-3": (p[1] + p[2] + p[3]) * 100,
        "2-3": (p[2] + p[3]) * 100,
        "2-4": (p[2] + p[3] + p[4]) * 100,
        "2-5": (p[2] + p[3] + p[4] + p[5]) * 100,
      };
      // ── Algoritmo Density-68 ────────────────────────────────────
      const qualifying = Object.entries(bands).filter(([_, v]) => v >= MIN_PROB);
      let best, reason;
      if (qualifying.length > 0) {
        // Massima densità P/width fra le fasce ≥ 68%
        qualifying.sort((a, b) => (b[1] / BAND_WIDTH[b[0]]) - (a[1] / BAND_WIDTH[a[0]]));
        best = qualifying[0];
        // Reason concisa per UX
        if (xg < 0.9) reason = "Tempo difensivo · xG basso";
        else if (xg < 1.6) reason = "Tempo equilibrato";
        else if (xg < 2.3) reason = "Tempo offensivo";
        else reason = "Tempo high-scoring · cautela";
      } else {
        // Fallback (tempi a xG molto alto, nessuna fascia ≥ 68%)
        const sorted = Object.entries(bands).sort((a, b) => b[1] - a[1]);
        best = sorted[0];
        reason = "Distribuzione gol molto larga · fallback max-prob";
      }
      return { band: best[0], prob: best[1], xg, reason };
    };
    const h1 = computeBands(goalsByHalf.first_half);
    const h2 = computeBands(goalsByHalf.second_half);
    return {
      h1Band: h1.band, h1Prob: h1.prob, h1Reason: h1.reason, h1Xg: h1.xg,
      h2Band: h2.band, h2Prob: h2.prob, h2Reason: h2.reason, h2Xg: h2.xg,
      combined: (h1.prob * h2.prob) / 100,
    };
  })();

  if (!teamCombo && !halfCombo) return null;

  return (
    <div
      data-testid="multigol-combo-strip"
      style={{
        marginTop: "0.25rem",
        padding: "0.85rem 0.95rem",
        background: "linear-gradient(90deg, rgba(34,255,163,0.05), rgba(167,139,250,0.05), rgba(255,111,216,0.05))",
        border: "1px solid rgba(167,139,250,0.30)",
        borderTop: "1px dashed rgba(167,139,250,0.45)",
        display: "flex",
        flexDirection: "column",
        gap: "0.65rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Sparkles size={12} style={{ color: "#A78BFA", filter: "drop-shadow(0 0 5px rgba(167,139,250,0.65))" }} />
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.68rem",
          letterSpacing: "0.26em",
          textTransform: "uppercase",
          color: "#A78BFA",
          fontWeight: 800,
          textShadow: "0 0 8px rgba(167,139,250,0.45)",
        }}>
          Combo Multigol · Più Probabili
        </span>
        <span style={{
          marginLeft: "auto",
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.55rem",
          color: "rgba(255,255,255,0.4)",
          letterSpacing: "0.14em",
        }}>
          Derivato dai dati sopra · indipendenza di mercato
        </span>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: teamCombo && halfCombo ? "1fr 1fr" : "1fr",
        gap: "0.7rem",
      }}>
        {teamCombo && (
          <ComboCard
            tag="CASA + TRASFERTA"
            tagColor={HOME_COLOR}
            leftLabel={`${homeTeam} ${teamCombo.hBand}`}
            leftProb={teamCombo.hProb}
            leftColor={HOME_COLOR}
            rightLabel={`${awayTeam} ${teamCombo.aBand}`}
            rightProb={teamCombo.aProb}
            rightColor={AWAY_COLOR}
            combined={teamCombo.combined}
            betLabel={`Multigol Casa ${teamCombo.hBand} + Multigol Ospite ${teamCombo.aBand}`}
            onAddBet={onAddBet}
            isSelected={isSelected}
            testId="combo-team"
          />
        )}
        {halfCombo && (
          <ComboCard
            tag="1° TEMPO + 2° TEMPO"
            tagColor={HT_COLOR}
            leftLabel={`Multigol 1T ${halfCombo.h1Band}`}
            leftProb={halfCombo.h1Prob}
            leftColor={HT_COLOR}
            rightLabel={`Multigol 2T ${halfCombo.h2Band}`}
            rightProb={halfCombo.h2Prob}
            rightColor={ST_COLOR}
            combined={halfCombo.combined}
            betLabel={`Multigol 1T ${halfCombo.h1Band} + Multigol 2T ${halfCombo.h2Band}`}
            onAddBet={onAddBet}
            isSelected={isSelected}
            testId="combo-half"
            footnote={`1T xG ${halfCombo.h1Xg.toFixed(2)} · ${halfCombo.h1Reason} → 2T xG ${halfCombo.h2Xg.toFixed(2)} · ${halfCombo.h2Reason}`}
          />
        )}
      </div>
    </div>
  );
}

function ComboCard({ tag, tagColor, leftLabel, leftProb, leftColor, rightLabel, rightProb, rightColor, combined, betLabel, onAddBet, isSelected, testId, footnote }) {
  const selected = isSelected ? isSelected(betLabel) : false;
  return (
    <div
      data-testid={testId}
      style={{
        background: "rgba(9,9,11,0.55)",
        border: `1px solid ${tagColor}40`,
        padding: "0.7rem 0.8rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.55rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.58rem",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: tagColor,
          fontWeight: 800,
          textShadow: `0 0 6px ${tagColor}60`,
        }}>
          {tag}
        </span>
        <span style={{
          marginLeft: "auto",
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.95rem",
          fontWeight: 800,
          color: "var(--bc-ink, #FFFFFF)",
          letterSpacing: "0.02em",
          textShadow: `0 0 8px ${tagColor}55`,
        }}>
          {combined.toFixed(1)}%
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap" }}>
        <ComboPill label={leftLabel} prob={leftProb} color={leftColor} />
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.7rem",
          color: "rgba(255,255,255,0.55)",
          fontWeight: 700,
        }}>×</span>
        <ComboPill label={rightLabel} prob={rightProb} color={rightColor} />
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.7rem",
          color: "rgba(255,255,255,0.55)",
          fontWeight: 700,
        }}>=</span>
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.78rem",
          fontWeight: 800,
          color: tagColor,
          textShadow: `0 0 6px ${tagColor}80`,
          letterSpacing: "0.04em",
        }}>
          {combined.toFixed(1)}%
        </span>
      </div>

      {footnote && (
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.55rem",
          letterSpacing: "0.06em",
          color: "rgba(255,255,255,0.45)",
          marginTop: "-0.2rem",
        }}>
          {footnote}
        </span>
      )}

      {onAddBet && (
        <button
          data-testid={`${testId}-add-btn`}
          onClick={() => onAddBet({ bet: betLabel, prob: combined, type: "multigol_combo" })}
          disabled={selected}
          style={{
            alignSelf: "flex-start",
            display: "flex",
            alignItems: "center",
            gap: "0.3rem",
            padding: "0.3rem 0.55rem",
            background: selected ? "var(--bc-border, rgba(255,255,255,0.07))" : `color-mix(in srgb, ${tagColor} 18%, var(--bc-panel, #0A0A0A))`,
            border: `1px solid ${selected ? "rgba(255,255,255,0.15)" : tagColor}`,
            color: selected ? "rgba(255,255,255,0.55)" : tagColor,
            fontFamily: "'Barlow Condensed',sans-serif",
            fontSize: "0.62rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontWeight: 700,
            cursor: selected ? "default" : "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <Plus size={10} />
          {selected ? "Aggiunto" : `Aggiungi combo ${combined.toFixed(1)}%`}
        </button>
      )}
    </div>
  );
}

function ComboPill({ label, prob, color }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
        padding: "0.22rem 0.5rem",
        background: `color-mix(in srgb, ${color} 12%, var(--bc-panel, #0A0A0A))`,
        border: `1px solid ${color}55`,
        fontFamily: "system-ui, sans-serif",
        fontSize: "0.68rem",
        color: "rgba(255,255,255,0.88)",
      }}
    >
      <span style={{ fontWeight: 600 }}>{label}</span>
      <span style={{
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.7rem",
        fontWeight: 800,
        color,
        textShadow: `0 0 4px ${color}80`,
      }}>
        {prob.toFixed(1)}%
      </span>
    </span>
  );
}


// Confidence band → qualitative badge. Thresholds calibrated against the
// backtest stratification (see PRD/CHANGELOG): ≥45 → 100% hit-rate,
// 35-45 → 89%, 25-35 → 80%, <25 → noisy.
const CONF_BADGES = [
  { min: 45, label: "PERFETTA", color: "#22FFA3", bg: "rgba(34,255,163,0.20)", glow: "rgba(34,255,163,0.55)" },
  { min: 35, label: "ALTA",     color: "#9DF6FF", bg: "rgba(157,246,255,0.18)", glow: "rgba(157,246,255,0.45)" },
  { min: 25, label: "MEDIA",    color: "#F6C643", bg: "rgba(246,198,67,0.15)",  glow: "rgba(246,198,67,0.35)" },
  { min: 15, label: "BASSA",    color: "#FF6FD8", bg: "rgba(255,111,216,0.13)", glow: "rgba(255,111,216,0.28)" },
  { min: 0,  label: "INCERTA",  color: "var(--bc-sub, #9CA3AF)", bg: "rgba(156,163,175,0.12)", glow: "rgba(156,163,175,0.18)" },
];

function _confBadge(conf) {
  if (typeof conf !== "number") return null;
  return CONF_BADGES.find((b) => conf >= b.min) || CONF_BADGES[CONF_BADGES.length - 1];
}


// ─── Hover info tooltip explaining Safe-Shift and Tail Risk chips ──
function SafetyInfoIcon() {
  const [open, setOpen] = useState(false);
  return (
    <span
      data-testid="goal-distribution-info"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      style={{
        position: "relative", display: "inline-flex", alignItems: "center",
        marginLeft: 4, cursor: "help",
      }}
    >
      <Info size={12} style={{
        color: "rgba(167,139,250,0.85)",
        filter: open ? "drop-shadow(0 0 6px rgba(167,139,250,0.8))" : "none",
        transition: "filter 0.15s ease",
      }} />
      {open && (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            zIndex: 50,
            width: 320,
            background: "var(--bc-panel, var(--bc-panel, #0A0A0A))",
            border: "1px solid rgba(167,139,250,0.55)",
            boxShadow: "0 8px 28px -6px rgba(167,139,250,0.45), 0 0 0 1px rgba(167,139,250,0.12) inset",
            padding: "0.85rem 0.95rem",
            fontFamily: "system-ui, sans-serif",
            color: "rgba(255,255,255,0.85)",
            fontSize: "0.72rem",
            lineHeight: 1.5,
          }}
        >
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontSize: "0.6rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "#A78BFA",
            fontWeight: 800,
            marginBottom: 8,
          }}>
            Glossario · Indicatori
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.66rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#22FFA3",
              fontWeight: 800,
              marginBottom: 3,
              textShadow: "0 0 6px rgba(34,255,163,0.4)",
            }}>
              ↑ SAFE-SHIFT
            </div>
            <div>
              Promozione automatica della banda <strong>0-2</strong> a <strong>1-3</strong> quando
              il rischio coda è alto e il margine è stretto. Cattura
              i match in cui la squadra fa 3 gol senza sacrificare troppo
              i casi a 0. Backtest 708 partite: <strong>+13 net</strong> partite vinte
              (19 saves vs 6 losses, ROI +15.3%).
            </div>
          </div>

          <div>
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.66rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#F6C643",
              fontWeight: 800,
              marginBottom: 3,
              textShadow: "0 0 6px rgba(246,198,67,0.4)",
            }}>
              ⚠ RISCHIO SFORAMENTO
            </div>
            <div>
              Probabilità che la squadra superi il limite della banda raccomandata
              (es. <strong>&gt;2 gol</strong> per 0-2, <strong>&gt;3 gol</strong> per 1-3).
              Il chip si accende solo se il rischio è ≥ <strong>18%</strong>.
              Sopra <strong>28%</strong> diventa magenta — banda fragile.
            </div>
          </div>
        </div>
      )}
    </span>
  );
}


function TeamMultigol({
  label, team, color, best, values, sideTag, testId,
  onAddBet, isSelected, betLabelPrefix, betType,
  confidence, factors, tailRisk, safeShift, bestRaw,
  bandOverflow, bandOverflowThreshold,
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: `color-mix(in srgb, ${color} 4%, var(--bc-panel, #0A0A0A))`,
        border: `1px solid ${color}40`,
        padding: "0.75rem 0.85rem",
        display: "flex", flexDirection: "column", gap: "0.55rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.55rem", letterSpacing: "0.18em",
          padding: "0.1rem 0.35rem", background: color, color: "#020202",
          fontWeight: 800,
        }}>
          {sideTag}
        </span>
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.95rem", fontWeight: 700, color: "var(--bc-ink, #FFFFFF)",
          letterSpacing: "0.04em",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {label} {team}
        </span>
        {typeof confidence === "number" && (() => {
          const badge = _confBadge(confidence);
          if (!badge) return null;
          return (
            <span
              data-testid={`${testId}-confidence`}
              title={`Sicurezza pronostico: ${badge.label} · Confidence ${confidence.toFixed(1)}%
≥45% PERFETTA (100% hit-rate nel backtest)
35-45% ALTA (89.3%)
25-35% MEDIA (80.0%)
15-25% BASSA · <15% INCERTA`}
              style={{
                marginLeft: "auto",
                display: "inline-flex", alignItems: "center", gap: "0.35rem",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: "0.6rem",
                letterSpacing: "0.2em",
                padding: "0.18rem 0.5rem",
                background: badge.bg,
                border: `1px solid ${badge.color}80`,
                color: badge.color,
                fontWeight: 800,
                textTransform: "uppercase",
                boxShadow: `0 0 8px ${badge.glow}, inset 0 0 6px ${badge.glow}`,
                textShadow: `0 0 6px ${badge.glow}`,
              }}>
              {badge.label}
              <span style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.54rem",
                opacity: 0.75,
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}>
                {confidence.toFixed(0)}%
              </span>
            </span>
          );
        })()}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.32rem" }}>
        {TEAM_BANDS.map((b) => {
          const value = Number(values[b.key] || 0);
          const isBest = best === b.bestKey;
          const betLabel = `${betLabelPrefix} ${b.bestKey} (${team})`;
          return (
            <Bar
              key={b.key}
              label={b.label}
              value={value}
              color={color}
              highlighted={isBest}
              onAddBet={onAddBet}
              isSelected={isSelected}
              betLabel={betLabel}
              betType={betType}
            />
          );
        })}
      </div>
      {/* Tail risk + safe-shift transparency row */}
      {(typeof bandOverflow === "number" && bandOverflow >= 18) || safeShift ? (
        <div data-testid={`${testId}-safety`} style={{
          display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center",
          marginTop: "-0.2rem",
        }}>
          {safeShift && (
            <span
              title={`Safe-shift: il modello ha promosso la banda da "${bestRaw || "0-2"}" a "${best}" perché il rischio coda P(team≥3) è alto e il margine era stretto. Riduce il rischio che la squadra faccia 3 gol e il pronostico salti.`}
              style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: "0.58rem", letterSpacing: "0.18em",
                padding: "0.14rem 0.42rem",
                background: "rgba(34,255,163,0.16)",
                border: "1px solid rgba(34,255,163,0.55)",
                color: "#22FFA3", fontWeight: 800,
                textTransform: "uppercase",
                boxShadow: "0 0 6px rgba(34,255,163,0.45)",
                textShadow: "0 0 5px rgba(34,255,163,0.5)",
              }}>
              ↑ Safe-Shift
            </span>
          )}
          {typeof bandOverflow === "number" && bandOverflow >= 18 && bandOverflowThreshold != null && (
            <span
              title={`Rischio sforamento: P(${team} > ${bandOverflowThreshold} gol) = ${bandOverflow.toFixed(1)}%.
Soglia di guardia ≥18%. Se la squadra fa più gol del limite della banda raccomandata (${best}), il pronostico salta.`}
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.58rem", letterSpacing: "0.12em",
                padding: "0.14rem 0.42rem",
                background: bandOverflow >= 28 ? "rgba(255,111,216,0.14)" : "rgba(246,198,67,0.13)",
                border: `1px solid ${bandOverflow >= 28 ? "#FF6FD8" : "#F6C643"}80`,
                color: bandOverflow >= 28 ? "#FF6FD8" : "#F6C643",
                fontWeight: 700,
              }}>
              ⚠ &gt;{bandOverflowThreshold} {bandOverflow.toFixed(0)}%
            </span>
          )}
        </div>
      ) : null}
      {factors && (
        <FactorChips color={color} factors={factors} testId={`${testId}-factors`} />
      )}
    </div>
  );
}


function FactorChips({ factors, color, testId }) {
  const tilt = Number(factors.tilt || 1);
  const tiltDir = tilt > 1.02 ? "↑" : tilt < 0.98 ? "↓" : "·";
  const chips = [
    { label: "xG", value: factors.team_xg?.toFixed?.(2) ?? factors.team_xg, hint: "Expected goals attesi (modello base)" },
    { label: "Form", value: factors.team_recent_scored?.toFixed?.(2) ?? factors.team_recent_scored, hint: "Media gol segnati ult. 5 partite" },
    { label: "Opp", value: factors.opp_recent_conceded?.toFixed?.(2) ?? factors.opp_recent_conceded, hint: "Media gol subiti dall'avversario ult. 5" },
    { label: `Tilt ${tiltDir}`, value: tilt.toFixed(2), hint: "Tilt multiplicativo applicato al modello base. >1 = sposta verso bande alte, <1 = verso bande basse" },
  ];
  return (
    <div data-testid={testId} style={{
      display: "flex", flexWrap: "wrap", gap: "0.3rem",
      paddingTop: "0.4rem",
      borderTop: `1px dashed ${color}28`,
    }}>
      {chips.filter((c) => c.value !== null && c.value !== undefined).map((c) => (
        <span
          key={c.label}
          title={c.hint}
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.56rem",
            letterSpacing: "0.08em",
            color: "rgba(255,255,255,0.55)",
            padding: "0.1rem 0.4rem",
            border: `1px solid ${color}25`,
            background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))",
          }}
        >
          <span style={{ color: `${color}CC`, fontWeight: 700, marginRight: 4 }}>{c.label}</span>
          {c.value}
        </span>
      ))}
    </div>
  );
}


function HalfCard({ label, tag, color, data, testId, onAddBet, isSelected }) {
  if (!data) return null;
  return (
    <div
      data-testid={testId}
      style={{
        background: `color-mix(in srgb, ${color} 4%, var(--bc-panel, #0A0A0A))`,
        border: `1px solid ${color}40`,
        padding: "0.75rem 0.85rem",
        display: "flex", flexDirection: "column", gap: "0.55rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.55rem", letterSpacing: "0.18em",
          padding: "0.1rem 0.35rem", background: color, color: "#020202",
          fontWeight: 800,
        }}>
          {tag}
        </span>
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.92rem", fontWeight: 700, color: "var(--bc-ink, #FFFFFF)",
          letterSpacing: "0.05em",
        }}>
          {label}
        </span>
        <div style={{ flex: 1 }} />
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.78rem", color, fontWeight: 800,
          textShadow: `0 0 8px ${color}80`,
        }}>
          {Number(data.total_xg || 0).toFixed(2)} <span style={{ fontSize: "0.6rem", opacity: 0.7 }}>xG</span>
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.32rem" }}>
        <Bar label="Over 0.5" value={data.over_05} color={color}
             onAddBet={onAddBet} isSelected={isSelected}
             betLabel={`Over 0.5 ${tag}`} betType="half_goal" />
        <Bar label="Over 1.5" value={data.over_15} color={color}
             onAddBet={onAddBet} isSelected={isSelected}
             betLabel={`Over 1.5 ${tag}`} betType="half_goal" />
        <Bar label={`Gol/Gol ${tag}`} value={data.btts}   color={color}
             onAddBet={onAddBet} isSelected={isSelected}
             betLabel={`Gol/Gol ${tag}`} betType="half_goal" />
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between",
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.6rem", letterSpacing: "0.08em",
        color: "rgba(255,255,255,0.45)",
        paddingTop: "0.2rem",
        borderTop: `1px dashed ${color}28`,
      }}>
        <span>Casa {Number(data.home_xg || 0).toFixed(2)} xG</span>
        <span>Ospite {Number(data.away_xg || 0).toFixed(2)} xG</span>
      </div>
    </div>
  );
}


function Bar({
  label, value, color, highlighted = false,
  onAddBet, isSelected, betLabel, betType,
}) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const clickable = !!(onAddBet && betLabel);
  const selected = clickable && isSelected && isSelected(betLabel);
  const barColor = highlighted ? color : `${color}80`;
  const glow = highlighted ? `0 0 10px ${color}, 0 0 18px ${color}40` : "none";
  const btnRef = useRef(null);

  const handleClick = (e) => {
    if (!clickable) return;
    e.stopPropagation();
    if (!selected) {
      import("@/lib/motion").then(({ flyToTarget }) => {
        const target = document.querySelector("[data-testid='nav-miei']")
          || document.querySelector("[data-testid='multipla-counter']");
        if (target && btnRef.current) flyToTarget(btnRef.current, target);
      }).catch(() => {});
    }
    onAddBet({ bet: betLabel, prob: Number(v.toFixed(1)), type: betType });
  };

  return (
    <button
      ref={btnRef}
      onClick={handleClick}
      disabled={!clickable}
      data-testid={`mg-bar-${label}`}
      data-selected={selected ? "true" : "false"}
      style={{
        display: "grid",
        gridTemplateColumns: "96px 1fr 58px",
        alignItems: "center",
        gap: "0.5rem",
        background: "transparent",
        border: "none",
        padding: "0.15rem 0.2rem",
        cursor: clickable ? "pointer" : "default",
        textAlign: "left",
        width: "100%",
        transition: "transform 0.15s ease, background 0.15s ease",
        transform: selected ? "translateX(2px)" : "none",
        outline: selected ? `1px solid ${color}` : "none",
        outlineOffset: selected ? 1 : 0,
      }}
      onMouseEnter={(e) => {
        if (clickable && !selected) e.currentTarget.style.background = `color-mix(in srgb, ${color} 8%, transparent)`;
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = "transparent";
      }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        fontFamily: "'Barlow Condensed',sans-serif",
        fontSize: "0.72rem",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: selected ? color : highlighted ? "#FFFFFF" : "rgba(255,255,255,0.65)",
        fontWeight: highlighted || selected ? 800 : 600,
      }}>
        {clickable && (
          selected
            ? <span style={{ color, fontWeight: 900 }}>&#10003;</span>
            : <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 700 }}>+</span>
        )}
        {label}
        {highlighted && <Trophy size={9} style={{ marginLeft: 2, color, verticalAlign: "middle" }} />}
      </span>
      <div style={{
        position: "relative",
        height: highlighted ? 10 : 7,
        background: "var(--bc-border, rgba(255,255,255,0.07))",
        border: `1px solid ${color}20`,
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          width: `${v}%`,
          background: `linear-gradient(90deg, ${barColor}, ${color})`,
          boxShadow: glow,
          transition: "width 0.5s ease-out",
        }} />
        {highlighted && (
          <div style={{
            position: "absolute", top: 0, bottom: 0,
            left: 0, width: "30%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
            animation: "shimmer 2.4s linear infinite",
          }} />
        )}
      </div>
      <span style={{
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: highlighted ? "0.78rem" : "0.7rem",
        color: selected ? color : highlighted ? color : "rgba(255,255,255,0.7)",
        fontWeight: highlighted || selected ? 800 : 600,
        letterSpacing: "0.04em",
        textAlign: "right",
        textShadow: highlighted || selected ? `0 0 8px ${color}90` : "none",
      }}>
        {v.toFixed(1)}%
      </span>
    </button>
  );
}
